import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader,
  Search,
  Trash2,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Ban,
  Users as UsersIcon,
  X,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 25;

const FILTERS = [
  { v: "", label: "Todos" },
  { v: "active", label: "Activos" },
  { v: "paid", label: "Pagados" },
  { v: "trial", label: "En prueba" },
  { v: "cancelled", label: "Cancelados" },
  { v: "expired", label: "Expirados" },
  { v: "banned", label: "Baneados" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso.slice(0, 10);
  }
}

function statusBadge(u) {
  if (u.banned) return { label: "Baneado", color: "#D9534F", bg: "#D9534F15" };
  const s = u.subscription_status;
  if (s === "canceled" || s === "cancelled")
    return { label: "Cancelado", color: "#B46A3C", bg: "#B46A3C18" };
  if (s === "incomplete_expired" || s === "unpaid")
    return { label: "Impago", color: "#B46A3C", bg: "#B46A3C18" };
  if (s === "lifetime_free")
    return { label: "Lifetime", color: "#6B9070", bg: "#6B907015" };
  if (u.has_paid && (s === "active" || s === "trialing" || !s))
    return { label: "Activo", color: "#6B9070", bg: "#6B907015" };
  if (!u.has_paid && u.trial_ends_at) {
    const future = new Date(u.trial_ends_at) > new Date();
    if (future)
      return { label: "Prueba", color: "#F0A82C", bg: "#F0A82C18" };
    return { label: "Expirado", color: "#8B6B50", bg: "#8B6B5018" };
  }
  return { label: "Sin plan", color: "#8B6B50", bg: "#8B6B5018" };
}

export default function UsersListPanel({ isPrimaryAdmin }) {
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null); // {id, email}
  const [testPreview, setTestPreview] = useState(null); // { total, sample }
  const [cleaningTest, setCleaningTest] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: String(page * PAGE_SIZE),
        limit: String(PAGE_SIZE),
      });
      if (status) params.set("status", status);
      const { data } = await axios.get(`${API}/admin/users?${params}`, {
        withCredentials: true,
      });
      setUsers(data.users || []);
      setTotal(data.total || 0);
    } catch {
      toast.error("No se pudo cargar la lista de usuarios");
    } finally {
      setLoading(false);
    }
  }, [status, page]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredUsers = search.trim()
    ? users.filter(
        (u) =>
          (u.email || "").toLowerCase().includes(search.toLowerCase()) ||
          (u.username || "").toLowerCase().includes(search.toLowerCase())
      )
    : users;

  const handleDelete = async (userId) => {
    setDeletingId(userId);
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      toast.success("Usuario eliminado");
      setConfirmDel(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo eliminar");
    } finally {
      setDeletingId(null);
    }
  };

  const previewTestUsers = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/test-users/preview`, {
        withCredentials: true,
      });
      setTestPreview(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo obtener la lista");
    }
  };

  const cleanTestUsers = async () => {
    setCleaningTest(true);
    try {
      const { data } = await axios.delete(`${API}/admin/test-users`, {
        withCredentials: true,
      });
      toast.success(
        `Se eliminaron ${data.deleted.users} usuarios de prueba y ${data.deleted.orphan_rooms} salas huérfanas`
      );
      setTestPreview(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo limpiar");
    } finally {
      setCleaningTest(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <section
      data-testid="users-list-panel"
      className="bg-[#FDFAF4] rounded-2xl border border-[#5B3E2E]/10 p-5 sm:p-6 mt-6"
    >
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <UsersIcon size={18} className="text-[#5B3E2E]/70" />
          <h2 className="text-lg font-heading font-bold text-[#5B3E2E]">
            Usuarios ({total})
          </h2>
        </div>
      </div>

      {/* Status filters */}
      <div className="flex gap-1.5 flex-wrap mb-3">
        {FILTERS.map((f) => (
          <button
            key={f.v || "all"}
            data-testid={`users-filter-${f.v || "all"}`}
            onClick={() => {
              setStatus(f.v);
              setPage(0);
            }}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              status === f.v
                ? "bg-[#E48D57] border-[#E48D57] text-white font-semibold"
                : "border-[#5B3E2E]/15 text-[#5B3E2E]/70 hover:bg-[#5B3E2E]/5"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5B3E2E]/40" />
        <input
          data-testid="users-search"
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nombre o email"
          className="w-full pl-9 pr-3 py-2 text-sm rounded-xl border border-[#5B3E2E]/15 bg-white text-[#5B3E2E] placeholder:text-[#5B3E2E]/35 focus:outline-none focus:border-[#E48D57]"
        />
      </div>

      {/* List */}
      {loading ? (
        <div className="py-10 flex justify-center">
          <Loader size={20} className="text-[#E48D57] animate-spin" />
        </div>
      ) : filteredUsers.length === 0 ? (
        <p className="py-10 text-center text-sm text-[#5B3E2E]/50">
          No hay usuarios con estos filtros.
        </p>
      ) : (
        <div className="space-y-1.5">
          {filteredUsers.map((u) => {
            const badge = statusBadge(u);
            return (
              <div
                key={u.id}
                data-testid={`user-row-${u.id}`}
                className="flex items-center gap-3 p-3 rounded-xl bg-white border border-[#5B3E2E]/8 hover:border-[#5B3E2E]/15 transition-colors"
              >
                <div className="w-9 h-9 rounded-full bg-[#E48D57]/15 flex items-center justify-center flex-shrink-0 font-semibold text-[#E48D57] text-sm">
                  {(u.username || u.email || "?").slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#5B3E2E] truncate">
                    {u.username || "—"}
                  </p>
                  <p className="text-xs text-[#5B3E2E]/50 truncate">{u.email}</p>
                  <p className="text-[10px] text-[#5B3E2E]/35 mt-0.5">
                    Alta: {fmtDate(u.created_at)}
                    {u.city && u.country ? ` · ${u.city}, ${u.country}` : u.country ? ` · ${u.country}` : ""}
                  </p>
                </div>
                <span
                  className="text-[10px] font-bold px-2 py-1 rounded-full tracking-wider uppercase"
                  style={{ color: badge.color, backgroundColor: badge.bg }}
                >
                  {badge.label}
                </span>
                {isPrimaryAdmin && (
                  <button
                    data-testid={`delete-user-${u.id}`}
                    onClick={() => setConfirmDel({ id: u.id, email: u.email, username: u.username })}
                    className="p-2 rounded-lg text-[#5B3E2E]/40 hover:text-[#D9534F] hover:bg-[#D9534F]/10 transition-colors flex-shrink-0"
                    title="Eliminar usuario"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-4 text-xs text-[#5B3E2E]/60">
          <button
            data-testid="users-prev-page"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="px-3 py-1.5 rounded-full border border-[#5B3E2E]/15 disabled:opacity-30 hover:bg-[#5B3E2E]/5"
          >
            ← Anterior
          </button>
          <span>
            Página {page + 1} de {totalPages}
          </span>
          <button
            data-testid="users-next-page"
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            className="px-3 py-1.5 rounded-full border border-[#5B3E2E]/15 disabled:opacity-30 hover:bg-[#5B3E2E]/5"
          >
            Siguiente →
          </button>
        </div>
      )}

      {/* Confirm delete user modal */}
      {confirmDel && (
        <div
          className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4"
          onClick={() => !deletingId && setConfirmDel(null)}
        >
          <div
            className="bg-[#FDFAF4] rounded-2xl p-5 max-w-sm w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={20} className="text-[#D9534F]" />
              <h3 className="font-heading text-lg text-[#5B3E2E]">
                ¿Eliminar usuario?
              </h3>
            </div>
            <p className="text-sm text-[#5B3E2E]/80 mb-2">
              {confirmDel.username ? <strong>{confirmDel.username}</strong> : null}{" "}
              <span className="text-[#5B3E2E]/55">({confirmDel.email})</span>
            </p>
            <p className="text-xs text-[#5B3E2E]/60 mb-4 leading-relaxed">
              Esta acción es irreversible. Se eliminarán también todas sus salas,
              mensajes, suscripciones push, sesiones y notificaciones.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => !deletingId && setConfirmDel(null)}
                className="flex-1 py-2 rounded-full border border-[#5B3E2E]/20 text-sm text-[#5B3E2E] hover:bg-[#5B3E2E]/5"
              >
                Cancelar
              </button>
              <button
                data-testid="confirm-delete-user"
                onClick={() => handleDelete(confirmDel.id)}
                disabled={!!deletingId}
                className="flex-1 py-2 rounded-full bg-[#D9534F] text-white text-sm font-semibold hover:bg-[#c0423e] disabled:opacity-60 flex items-center justify-center gap-1.5"
              >
                {deletingId ? <Loader size={14} className="animate-spin" /> : <Trash2 size={14} />}
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Test users cleanup preview modal */}
      {testPreview && (
        <div
          className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4"
          onClick={() => !cleaningTest && setTestPreview(null)}
        >
          <div
            className="bg-[#FDFAF4] rounded-2xl p-5 max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-2 mb-3">
              <div className="flex items-center gap-2">
                <AlertTriangle size={20} className="text-[#D9534F]" />
                <h3 className="font-heading text-lg text-[#5B3E2E]">
                  Limpiar usuarios de prueba
                </h3>
              </div>
              <button
                onClick={() => !cleaningTest && setTestPreview(null)}
                className="text-[#5B3E2E]/40 hover:text-[#5B3E2E] p-1"
              >
                <X size={16} />
              </button>
            </div>
            <p className="text-sm text-[#5B3E2E]/85 mb-2">
              Se eliminarán <strong>{testPreview.total}</strong> cuentas de prueba
              (incluye emails <code className="text-xs">@example.com</code>,{" "}
              <code className="text-xs">@test.com</code> y usuarios con nombre{" "}
              <code className="text-xs">TEST_...</code>) junto con todas sus salas,
              mensajes y notificaciones.
            </p>
            <p className="text-[11px] text-[#5B3E2E]/55 mb-3">
              Muestra de los primeros 20:
            </p>
            <div className="bg-[#5B3E2E]/5 rounded-xl p-2 max-h-40 overflow-y-auto mb-4">
              {testPreview.sample.map((s) => (
                <p
                  key={s.id}
                  className="text-[11px] text-[#5B3E2E]/65 truncate font-mono"
                >
                  {s.email}
                </p>
              ))}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => !cleaningTest && setTestPreview(null)}
                className="flex-1 py-2 rounded-full border border-[#5B3E2E]/20 text-sm text-[#5B3E2E] hover:bg-[#5B3E2E]/5"
              >
                Cancelar
              </button>
              <button
                data-testid="confirm-clean-test-users"
                onClick={cleanTestUsers}
                disabled={cleaningTest || testPreview.total === 0}
                className="flex-1 py-2 rounded-full bg-[#D9534F] text-white text-sm font-semibold hover:bg-[#c0423e] disabled:opacity-60 flex items-center justify-center gap-1.5"
              >
                {cleaningTest ? (
                  <Loader size={14} className="animate-spin" />
                ) : (
                  <Trash2 size={14} />
                )}
                Eliminar {testPreview.total}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
